import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type OpenCustomerQuoteResult } from '@whinerp/client';
import { computeQuoteTotals } from '@whinerp/contracts/money';
import { formatDate, formatMoney } from '@whinerp/contracts/format';
import { Button, Num, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * One quote, as the customer reads and decides it (F-048, F-159).
 *
 * The UX pointer is the design: "the running total must update as the
 * customer ticks. A static list of extras with a price is a brochure; a live
 * total is an upsell." The total is worked out with the same arithmetic the
 * quote builder's grid uses, from the lines the server sent - and once they
 * decide, the server's total replaces it.
 */

type Quote = NonNullable<OpenCustomerQuoteResult['quote']>;

export function CustomerQuote({ token, quoteId }: { token: string; quoteId: string }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const quote = useQuery<OpenCustomerQuoteResult, ApiError>({
    queryKey: ['portal', 'quote', token, quoteId],
    queryFn: () => api.openCustomerQuote({ path: { quoteId }, query: { token } }),
  });

  const decide = useMutation<OpenCustomerQuoteResult, ApiError, 'accept' | 'decline'>({
    mutationFn: (decision) =>
      api.decideCustomerQuote({
        path: { quoteId },
        body: {
          token,
          decision,
          optionalLineIds: decision === 'accept' ? [...ticked] : [],
          reason: decision === 'decline' && reason.trim() ? reason : null,
        },
      }),
    onSuccess: (result) => queryClient.setQueryData(['portal', 'quote', token, quoteId], result),
  });

  if (quote.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (quote.isError) return <p className="text-dense text-danger-text">{errorText(quote.error)}</p>;
  const data: Quote | null = quote.data.quote;
  if (!data) return <p className="text-dense text-danger-text">{t('portal.quote.unavailable')}</p>;

  const money = (amount: string): string => formatMoney(amount, format, { currency: data.totals.currency });
  const live = data.decidable
    ? computeQuoteTotals(
        data.lines.map((line) => ({ ...line, optional: line.optional && !ticked.has(line.lineId) })),
        data.totals.discountPercent,
      ).total
    : data.totals.total;
  const required = data.lines.filter((line) => !line.optional);
  const optional = data.lines.filter((line) => line.optional);

  return (
    <div data-customer-quote={data.quoteId} data-customer-quote-status={data.status} className="flex flex-col gap-3">
      {data.validUntil ? (
        <p className="text-meta text-ink-meta">
          {data.lapsed
            ? t('portal.quote.lapsed', { values: { date: formatDate(data.validUntil, format) } })
            : t('portal.quote.validUntil', { values: { date: formatDate(data.validUntil, format) } })}
        </p>
      ) : null}

      <ul className="flex flex-col">
        {required.map((line) => (
          <li key={line.lineId} className="flex items-baseline justify-between gap-3 border-t border-t-rule py-1 first:border-t-0">
            <span className="min-w-0 flex-1 text-dense text-ink">
              {line.description}
              <span className="ms-2 text-meta text-ink-meta">× {line.quantity}</span>
            </span>
            <Num align="end" className="text-dense text-ink">
              {money(line.lineTotal)}
            </Num>
          </li>
        ))}
      </ul>

      {optional.length > 0 ? (
        <section data-customer-quote-optional className="flex flex-col gap-1 rounded-card bg-sunken p-3">
          <span className="text-meta text-ink-meta">{t('portal.quote.optionalTitle')}</span>
          {optional.map((line) => (
            <label key={line.lineId} className="flex items-baseline gap-2 text-dense text-ink">
              <input
                type="checkbox"
                data-optional-line={line.lineId}
                disabled={!data.decidable}
                checked={data.decidable ? ticked.has(line.lineId) : line.chosen}
                onChange={(event) =>
                  setTicked((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(line.lineId);
                    else next.delete(line.lineId);
                    return next;
                  })
                }
              />
              <span className="min-w-0 flex-1">{line.description}</span>
              <Num align="end">{money(line.lineTotal)}</Num>
            </label>
          ))}
        </section>
      ) : null}

      <p className="flex items-baseline justify-between gap-3 border-t border-t-rule-strong pt-2">
        <span className="text-body font-medium text-ink">{t('quote.total')}</span>
        <Num align="end" data-customer-quote-total className="text-body font-medium text-ink">
          {money(live)}
        </Num>
      </p>
      {data.terms ? <p className="whitespace-pre-wrap text-meta text-ink-meta">{data.terms}</p> : null}

      {decide.error ? (
        <p data-customer-quote-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {errorText(decide.error)}
        </p>
      ) : null}

      {data.decidable ? (
        declining ? (
          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('portal.quote.declineReason')}
              <textarea
                data-customer-quote-reason
                rows={3}
                className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button intent="danger" size="small" data-customer-quote-confirm-decline disabled={decide.isPending} onClick={() => decide.mutate('decline')}>
                {t('portal.quote.decline')}
              </Button>
              <Button intent="ghost" size="small" onClick={() => setDeclining(false)}>
                {t('portal.quote.back')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button intent="primary" size="small" data-customer-quote-accept disabled={decide.isPending} onClick={() => decide.mutate('accept')}>
              {t('portal.quote.accept', { values: { total: money(live) } })}
            </Button>
            <Button intent="ghost" size="small" data-customer-quote-decline onClick={() => setDeclining(true)}>
              {t('portal.quote.decline')}
            </Button>
          </div>
        )
      ) : data.decidedAt ? (
        <p data-customer-quote-decided className="text-dense text-ink">
          {t(data.status === 'accepted' ? 'portal.quote.accepted' : 'portal.quote.declined', {
            values: { date: formatDate(data.decidedAt, format) },
          })}
        </p>
      ) : null}
    </div>
  );
}
